import PropTypes from 'prop-types';
import Actions from 'webapp/actions/actions';
import React from 'react';
import EligibilityInformationDetail from 'webapp/components/customer/eligibility/eligibility_infomation_detail';
import ActionWithLoading from 'webapp/components/shared/action_with_loading';
import {
  applicant,
  applicantEligibility,
  coApplicant,
  coApplicantEligibility,
} from 'webapp/components/customer/config/form_fields';
import Anchor from '../shared/anchor';
import QuickCheckNameRow from './quickcheck_name_row';
import { isEffectivelyEmpty } from 'webapp/helpers/string_helper';
import LocationHelper from 'webapp/helpers/location_helper';
import BWComponent from '../BWComponent';
import PhoneFormatInput from '../shared/phone_format_input';
import CustomerHelper from '../../helpers/customer_helper';
import RecaptchaWidget from './recaptcha_widget';
import QuickCheckShare from './share/quickcheck_share';
import auth_helper from 'webapp/helpers/auth_helper';
import MaterialAddressAutocomplete from '../shared/material_address_autocomplete';
import { TextField } from '@mui/material';

export class QuickCheckPage extends BWComponent {
  static propTypes = {
    onChange: PropTypes.func,
    isInstallerUser: PropTypes.bool.isRequired,
    user: PropTypes.object,
  };

  constructor(props) {
    super(props, ['formValues'], {
      goBackProcessing: false,
      quickCheckProcessing: false,
      formValues: {
        [applicant.firstName]: null,
        [applicant.lastName]: null,
        [applicant.address]: null,
        [applicant.email]: null,
        [applicant.phone]: null,
        [applicant.city]: null,

        [applicantEligibility.dateOfBirth]: null,
        [applicantEligibility.social]: null,
        [applicantEligibility.annualIncome]: null,

        [coApplicant.firstName]: null,
        [coApplicant.lastName]: null,
        [coApplicant.email]: null,
        [coApplicant.phone]: null,

        [coApplicantEligibility.dateOfBirth]: null,
        [coApplicantEligibility.social]: null,
        [coApplicantEligibility.annualIncome]: null,
      },
      validSubmission: true,
      coAppOpen: false,
      isInvalid: {
        [applicant.firstName]: '',
        [applicant.lastName]: '',
        [applicant.address]: '',
        [applicant.email]: '',
        [applicant.phone]: '',

        [applicantEligibility.dateOfBirth]: '',
        [applicantEligibility.social]: '',
        [applicantEligibility.annualIncome]: '',

        [coApplicant.firstName]: '',
        [coApplicant.lastName]: '',

        [coApplicant.email]: '',
        [coApplicant.phone]: '',

        [coApplicantEligibility.dateOfBirth]: '',
        [coApplicantEligibility.social]: '',
        [coApplicantEligibility.annualIncome]: '',
      },
      recaptchaAction: 'zip-lookup', // "zip-lookup" or "submit-quick-check"
      recaptchaVerifyData: null,
      recaptchaRef: null,
      recaptchaToken: null,
    });
    this.zipRef = React.createRef();
    this.state.coAppOpen = this.hasCoApp(this.state.formValues);
  }

  hasCoApp(formValues) {
    return !!(
      formValues[coApplicant.firstName] ||
      formValues[coApplicant.lastName] ||
      formValues[coApplicant.email] ||
      formValues[coApplicant.phone] ||
      formValues[coApplicantEligibility.dateOfBirth] ||
      formValues[coApplicantEligibility.social] ||
      formValues[coApplicantEligibility.annualIncome]
    );
  }

  async componentDidMount() {
    await this.changeZip({ target: { setCustomValidity: () => {}, value: LocationHelper.searchAsObj().czip } });
  }

  onRecaptchaLoad = (recaptchaRef) => {
    this.setState({ recaptchaRef });
  };

  onRecaptchaVerify = async (token) => {
    this.setState({ recaptchaToken: token });
    if (this.state.recaptchaAction === 'zip-lookup') {
      await this.fetchZip(this.state.recaptchaVerifyData);
    } else if (this.state.recaptchaAction === 'submit-quick-check') {
      await this.preScreen();
    }
  };

  onRecaptchaExpire = () => {
    this.displayRecaptchaValidationError();
  };

  displayRecaptchaValidationError = () => {
    Actions.setErrorMessages(['Your session has timed out. Please reload this page.']);
  };

  changeZip = async ({ target }) => {
    const { value } = target;

    target.setCustomValidity('');
    this.setState({ zipCodeError: '' });

    this.setState(
      {
        formValues: Object.assign({}, this.state.formValues, {
          [applicant.zip]: value,
        }),
      },
      () => {
        if ((this.zipRef.current.value + '').length === 5) {
          this.blurZip();
        }
      }
    );
  };

  blurZip = async () => {
    const setZipCodeError = (err) => {
      this.zipRef.current.setCustomValidity(err);
      this.setState({ zipCodeError: err });
    };

    if (this.zipRef.current.value.length === 5) {
      const recaptchaVerifyData = { zip: this.zipRef.current.value, setZipCodeError };
      if (this.skipCaptcha()) {
        // if it is an installer or if we've already done recaptcha, then just go fetch the zip
        await this.fetchZip(recaptchaVerifyData);
      } else {
        this.setState({ recaptchaVerifyData });
        this.state.recaptchaRef.execute();
      }
    } else {
      return setZipCodeError('ZIP Code must be 5 digits');
    }
  };

  fetchZip = async ({ zip, setZipCodeError }) => {
    try {
      let location = await Actions.fetchZip({ zip, setZipCodeError });

      this.setState({
        formValues: Object.assign({}, this.state.formValues, {
          ccity: this.state.formValues.ccity || location.city,
          cstate: location.state || this.state.formValues.cstate,
        }),
      });

      this.reRenderUrl();
    } catch (e) {
      if (e.code === 401) {
        this.displayRecaptchaValidationError();
        return;
      }
      return setZipCodeError('Could not find ZIP Code');
    }
  };

  // Regex matches if name doesn't start with a "-" and <>:@/ are not in the input
  checkFieldValidity = (e, input) => {
    // MaterialAddressAutoComplete triggers an event with an undefined target :(
    if (e?.target === undefined || e.target.value === '') {
      e.target = { name: 'default' };
    }

    return '';
  };

  checkValidSubmission = (isInvalid) => {
    if (!isInvalid) {
      isInvalid = this.state.isInvalid;
    }

    return Object.values(isInvalid).every((v) => !v);
  };

  onChange = (e, name, value, next) => {
    let { formValues, isInvalid } = this.state;

    let tmpFormValue = JSON.parse(JSON.stringify(formValues));
    let tmpIsValid = JSON.parse(JSON.stringify(isInvalid));

    name = name || e.target.name;
    value = value || e.target.value;
    tmpFormValue[name] = value;
    tmpIsValid[name] = this.checkFieldValidity(e, value);

    let isCoAppField = [...Object.values(coApplicant), Object.values(coApplicantEligibility)].indexOf(name) > -1;
    let coAppHasErrors = Object.values(coApplicant).some((fieldName) => isInvalid[fieldName]);

    if (isCoAppField && coAppHasErrors) {
      tmpIsValid = this._checkCoAppValidity(tmpFormValue, tmpIsValid);
    }

    let isPrimaryAppField = [...Object.values(applicant), Object.values(applicantEligibility)].indexOf(name) > -1;
    let primaryAppHasErrors = Object.values(applicant).some((fieldName) => isInvalid[fieldName]);

    if (isPrimaryAppField && primaryAppHasErrors) {
      tmpIsValid = this._checkAppValidity(tmpFormValue, tmpIsValid);
    }

    let validSubmission = this.checkValidSubmission(tmpIsValid) && !tmpIsValid[name];

    this.setState({ formValues: tmpFormValue, isInvalid: tmpIsValid, validSubmission: validSubmission }, next);
  };

  onBlur = () => {
    this.reRenderUrl();
  };

  _checkCoAppValidity = (formValues, isInvalid) => {
    return this.checkGenericApplicantValidity(
      formValues,
      isInvalid,
      coApplicant,
      coApplicantEligibility,
      'Cannot be blank if other co-app fields are filled out'
    );
  };

  _checkAppValidity = (formValues, isInvalid) => {
    return this.checkGenericApplicantValidity(
      formValues,
      isInvalid,
      applicant,
      applicantEligibility,
      'Cannot be blank if other fields are filled out'
    );
  };

  checkApplicantsValidity = (formValues, isInvalid) => {
    let newInvalid = isInvalid;
    newInvalid = this._checkAppValidity(formValues, newInvalid);
    newInvalid = this._checkCoAppValidity(formValues, newInvalid);
    this.setState({ isInvalid: newInvalid });
    return newInvalid;
  };

  checkGenericApplicantValidity = (formValues, isInvalid, applicant, applicantEligibility, err) => {
    if (!isInvalid) {
      isInvalid = this.state.isInvalid;
    }
    const { firstName, lastName, email, phone } = applicant;
    const errorMessage = err || 'Cannot be blank if other co-app fields are filled out';
    let newInvalid = JSON.parse(JSON.stringify(isInvalid)); // dont use same mem
    newInvalid[firstName] = '';
    newInvalid[lastName] = '';
    let fieldNames = [...Object.values(applicantEligibility), firstName, lastName, email, phone];
    if (
      fieldNames.some((coAppEligField) => {
        let isNotEmpty = !isEffectivelyEmpty(formValues[coAppEligField]);
        // console.log('field', coAppEligField, 'isNotEmpty', isNotEmpty)
        return isNotEmpty;
      })
    ) {
      if (isEffectivelyEmpty(formValues[firstName])) {
        newInvalid[firstName] = errorMessage;
      }
      if (isEffectivelyEmpty(formValues[lastName])) {
        newInvalid[lastName] = errorMessage;
      }
    } else {
      fieldNames.forEach((field) => {
        newInvalid[field] = '';
      });
    }
    fieldNames.forEach((field) => {
      let value = formValues[field];
      if (!field.includes('email')) {
        if (
          !(
            [...Object.values(applicantEligibility), ...Object.values(coApplicantEligibility)].indexOf(field) > -1 ||
            /^(?! *-)((?![<>@:\\\\]).)*$/.test(value)
          )
        ) {
          newInvalid[field] = 'Field must not contain < > : @ or start with "-"';
        }
      }
      if (field.includes('email') && value && !CustomerHelper.validateEmail(value)) {
        newInvalid[field] = 'Field must be in the format of name@example.com';
      }
      if (field.includes('phone') && value && value.length < 14) {
        newInvalid[field] = 'Field must be in the format of (123) 456-7890';
      }
    });
    /* eslint-disable no-unused-expressions */
    this.form.querySelector(`input[name="${firstName}"]`)?.setCustomValidity(newInvalid[firstName]);
    this.form.querySelector(`input[name="${lastName}"]`)?.setCustomValidity(newInvalid[lastName]);
    /* eslint-enable no-unused-expressions */
    return newInvalid;
  };

  preScreenWithRecaptcha = async (e) => {
    e.preventDefault();
    if (this.skipCaptcha()) {
      // if this is an installer or we've already done recaptcha, then just go prescreen
      await this.preScreen();
    } else {
      this.setState({ recaptchaAction: 'submit-quick-check' });
      this.state.recaptchaRef.execute();
    }
  };

  skipCaptcha() {
    return this.props.isInstallerUser || !!this.state.recaptchaToken || this.getUrlState('captcha') === 'false';
  }

  preScreen = async () => {
    let invalidNew = this.checkApplicantsValidity(this.state.formValues, this.state.isInvalid);

    if (!this.checkValidSubmission(invalidNew)) {
      return this.setValidationError();
    }
    if (!this.form.checkValidity()) {
      return this.setValidationError();
    }

    this.setState({ quickCheckProcessing: true });

    let prescreenPayload = Object.assign({}, this.state.formValues, {
      [applicant.firstName]: this.state.formValues[applicant.firstName],
      [applicant.lastName]: this.state.formValues[applicant.lastName],
      [applicant.phone]: this.state.formValues[applicant.phone]
        ? (this.state.formValues[applicant.phone] || '').replace(/[^\d]/g, '', '')
        : undefined,
      [applicant.email]: this.state.formValues[applicant.email],
      [applicant.zip]: this.state.formValues[applicant.zip],
      [coApplicant.phone]: this.state.formValues[coApplicant.phone]
        ? (this.state.formValues[coApplicant.phone] || '').replace(/[^\d]/g, '', '')
        : undefined,
      [applicant.city]: this.state.formValues[applicant.city],
      [applicant.state]: this.state.formValues[applicant.state],
      [applicant.campaignId]:
        typeof this.getUrlState('r') === 'string' && this.getUrlState('r') !== '' ? this.getUrlState('r') : null,
      [applicant.source]: {
        undefined: undefined,
        GENERIC_MARKETING: 1,
        INDIVIDUAL_MARKETING: 2,
      }[this.getUrlState('marketType')],
    });

    try {
      let response = await Actions.preScreenCustomer(prescreenPayload);
      if (!response) {
        Actions.setErrorMessages(['An error occurred creating customer']);
        throw new Error('An error occurred creating customer');
      }
      let { customerId, proposalId, financingMethodId } = response;
      if (auth_helper.isShortAuth()) {
        Actions.setView({ page: 'quickCheckRemoteCompleted', proposalId });
        Actions.setRoute(
          `quickCheckRemoteCompleted?companyName=${this.getUrlState('companyName')}&installerName=${this.getUrlState(
            'installerName'
          )}`
        );
        return;
      }
      Actions.beginPollPrescreen(customerId, async () => {
        let route = `/proposal/${proposalId}/financing-method/${financingMethodId}/quickCheckStatus`;
        console.log('headed to', route, customerId, proposalId, financingMethodId);
        Actions.setRoute(route);

        await Actions.fetchCustomer(customerId);
        Actions.setView({ page: 'quickCheckStatus', proposalId });
      });
    } catch (e) {
      if (e.code === 401) {
        this.displayRecaptchaValidationError();
        return;
      }
      console.error('quickcheck error', e);
    }
  };

  setValidationError = () => {
    let errorMessage = 'Please enter required information';
    const firstInvalidElement = this.form.querySelector(':invalid');
    if (firstInvalidElement) {
      firstInvalidElement.focus();
      if (!firstInvalidElement.required) {
        errorMessage = 'Please provide a valid value';
      }
    }
    Actions.setErrorMessages([errorMessage]);
    return false;
  };

  renderButtons = () => {
    let classNameCheckNow = 'check-now pull-right';
    let checkNowButtonText = 'Check Now';
    if (this.state.quickCheckProcessing) {
      classNameCheckNow = 'check-now pull-right processing-with-text';
      checkNowButtonText = 'Checking for financing options';
    }

    return (
      <div className="button-container row">
        {!this.props.isInstallerUser && (
          <RecaptchaWidget
            onLoad={this.onRecaptchaLoad}
            onVerify={this.onRecaptchaVerify}
            onExpire={this.onRecaptchaExpire}
            action={this.state.recaptchaAction}
          />
        )}
        <ActionWithLoading
          processing={this.state.quickCheckProcessing}
          classNames={classNameCheckNow}
          buttonType="submit"
          disabled={!this.state.validSubmission}
        >
          {checkNowButtonText}
        </ActionWithLoading>
      </div>
    );
  };

  onClickShowHideCoApp = (e) => {
    e.preventDefault();

    let newIsInvalid = this.withoutCoAppErrors();
    let newFormValues = this.withoutCoAppForm();

    let validSubmission = this.checkValidSubmission(newIsInvalid);

    this.setState({
      coAppOpen: !this.state.coAppOpen,
      formValues: newFormValues,
      isInvalid: newIsInvalid,
      validSubmission,
    });
  };

  withoutCoAppErrors() {
    return Object.assign(
      {},
      this.state.isInvalid,
      Object.values(coApplicant).reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {}),
      Object.values(coApplicantEligibility).reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {})
    );
  }

  withoutCoAppForm() {
    return Object.assign(
      {},
      this.state.formValues,
      Object.values(coApplicant).reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {}),
      Object.values(coApplicantEligibility).reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {})
    );
  }

  render() {
    const { formValues, quickCheckProcessing, isInvalid } = this.state;
    const { isInstallerUser } = this.props;

    return (
      <>
        <div className="quick-check quick-check-page">
          <div className="backdrop">
            <div className="content">
              <div className="quick-check-header">
                <div className="header">
                  {!isInstallerUser && this.getUrlState('companyName') && this.getUrlState('installerName') && (
                    <div>
                      <div className="quick-check-lead-company">
                        <span style={{ fontWeight: 'bold', fontSize: 'medium' }}>Sales Representative: </span>
                        <span style={{ fontWeight: 'bold', fontSize: 'medium' }} className={'qa-form-generated-by'}>
                          {' '}
                          {this.getUrlState('installerName')}, {this.getUrlState('companyName')}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="quick-check-title">QuickCheck</div>
                  {isInstallerUser && <QuickCheckShare />}
                </div>
                <div className="subtitle">
                  {isInstallerUser ? (
                    "Enter the homeowner's name and address to see if they pre-qualify for financing."
                  ) : (
                    <>
                      <p>
                        Complete the form below to see if you are pre-qualified for home efficiency financing through
                        Concert Finance.
                      </p>
                      <p style={{ fontWeight: 'bold', fontSize: 'medium' }}>
                        Submitting this form will not affect your credit score.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <form
                name="quick-check-form"
                className="applicant-info-form"
                autoComplete="off"
                onSubmit={this.preScreenWithRecaptcha}
                noValidate={true}
                ref={(form) => {
                  this.form = form;
                }}
              >
                <QuickCheckNameRow
                  {...{
                    formFields: applicant,
                    required: true,
                    formValues,
                    isInvalid,
                    processing: quickCheckProcessing,
                    onChange: this.onChange,
                    onBlur: this.onBlur,
                  }}
                />
                <div className="flex-row">
                  <TextField
                    className="text-field"
                    name={applicant.phone}
                    id={applicant.phone}
                    required={!isInstallerUser}
                    disabled={quickCheckProcessing}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    maxLength="14"
                    minLength="14"
                    fullWidth={true}
                    size="medium"
                    variant="outlined"
                    label="Mobile Phone"
                    value={formValues[applicant.phone] || ''}
                    InputLabelProps={{ shrink: !!formValues[applicant.phone] }}
                    InputProps={{
                      inputComponent: PhoneFormatInput,
                    }}
                    error={!!this.state.isInvalid[applicant.phone]}
                    helperText={
                      this.state.isInvalid[applicant.phone] && this.state.isInvalid[applicant.phone]
                        ? this.state.isInvalid[applicant.phone]
                        : ''
                    }
                  />
                  <TextField
                    label="Email"
                    name={applicant.email}
                    id={applicant.email}
                    required={!isInstallerUser}
                    disabled={quickCheckProcessing}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    type="text"
                    maxLength="5"
                    minLength="50"
                    value={formValues[applicant.email] || ''}
                    variant="outlined"
                    className="text-field"
                    fullWidth
                    error={!!this.state.isInvalid[applicant.email]}
                    helperText={this.state.isInvalid[applicant.email] ? this.state.isInvalid[applicant.email] : ''}
                  />
                </div>
                <div className="flex-row">
                  <MaterialAddressAutocomplete
                    label="Address"
                    name={applicant.address}
                    id={applicant.address}
                    required
                    disabled={quickCheckProcessing}
                    zip={formValues[applicant.zip]}
                    value={formValues[applicant.address] || ''}
                    onChange={this.onChange}
                    variant="outlined"
                    className="text-field"
                    onAutocompleteSelect={async (streetAddress, zip, city, state) => {
                      const cityOverride = city ?? (await Actions.fetchZip({ zip })?.city) ?? '';
                      this.onChange({ target: { value: streetAddress } }, applicant.address, streetAddress, () => {
                        this.onChange({ target: { value: city } }, applicant.city, cityOverride, () => {
                          this.onChange({ target: { value: zip } }, applicant.zip, zip, () => {
                            this.onChange({ target: { value: state } }, applicant.state, state);
                          });
                        });
                      });
                    }}
                    large={true}
                    fullWidth
                    error={!!isInvalid[applicant.address]}
                    helperText={isInvalid[applicant.address]}
                  />
                </div>
                <div className="flex-row extra-address-fields">
                  <TextField
                    label="City"
                    name={applicant.city}
                    id={applicant.city}
                    required
                    maxLength="30"
                    value={formValues[applicant.city] || ''}
                    variant="outlined"
                    onChange={this.onChange}
                    className="text-field city-input"
                    margin="normal"
                    fullWidth
                  />
                  <TextField
                    label="State"
                    name={applicant.state}
                    id={applicant.state}
                    required
                    disabled
                    value={formValues[applicant.state] || ''}
                    InputLabelProps={{ required: false }}
                    variant="outlined"
                    className="text-field state-input"
                    margin="normal"
                    fullWidth
                  />

                  <TextField
                    inputRef={this.zipRef}
                    label="Zip Code"
                    name={applicant.zip}
                    id={applicant.zip}
                    required
                    disabled={quickCheckProcessing}
                    onChange={(e) => {
                      e.target.value = e?.target?.value?.replace(/[^0-9]/g, '');
                      this.changeZip(e);
                    }}
                    onBlur={this.blurZip}
                    type="text"
                    value={formValues[applicant.zip] || ''}
                    margin="normal"
                    variant="outlined"
                    className="text-field non-navigable-number zip-input"
                    fullWidth
                    error={!!this.state.zipCodeError}
                    helperText={this.state.zipCodeError && this.state.zipCodeError ? this.state.zipCodeError : ''}
                    inputProps={{
                      className: 'mui-input',
                      disabled: quickCheckProcessing,
                      maxLength: 5,
                    }}
                  />
                </div>
                <EligibilityInformationDetail
                  {...{
                    formFields: applicantEligibility,
                    onChange: this.onChange,
                    onBlur: this.onBlur,
                    dense: false,
                    formOptions: {
                      dateOfBirth: {
                        required: false,
                        label: 'Date of Birth (MM/DD/YYYY - optional)',
                        initialValue: this.state.formValues[applicantEligibility.dateOfBirth],
                      },
                      social: {
                        required: true,
                        label: 'Last 4 digits of SSN (e.g. 6789) *',
                        digits: 4,
                        initialValue: this.state.formValues[applicantEligibility.social],
                      },
                      annualIncome: {
                        required: false,
                        label: 'Annual Income (optional)¹',
                        initialValue: this.state.formValues[applicantEligibility.annualIncome],
                      },
                    },
                  }}
                />
                {this.state.coAppOpen && (
                  <>
                    <Anchor className="remove-button remove-coapp-button" onClick={this.onClickShowHideCoApp}>
                      <span className="minus">-</span>
                      <div className="label">Co-applicant</div>
                    </Anchor>
                    <div className="co-applicant-information">
                      <QuickCheckNameRow
                        {...{
                          formFields: coApplicant,
                          required: false,
                          formValues,
                          isInvalid,
                          processing: quickCheckProcessing,
                          onChange: this.onChange,
                          onBlur: this.onBlur,
                        }}
                      />
                      <div className="flex-row">
                        <TextField
                          className="text-field"
                          name={coApplicant.phone}
                          id={coApplicant.phone}
                          required={false}
                          disabled={quickCheckProcessing}
                          onChange={this.onChange}
                          onBlur={this.onBlur}
                          maxLength="14"
                          minLength="14"
                          fullWidth={true}
                          size="medium"
                          margin="normal"
                          variant="outlined"
                          label="Phone"
                          value={formValues[coApplicant.phone] || ''}
                          InputLabelProps={{ shrink: !!formValues[coApplicant.phone] }}
                          InputProps={{
                            inputComponent: PhoneFormatInput,
                          }}
                          error={!!this.state.isInvalid[coApplicant.phone]}
                          helperText={
                            this.state.isInvalid[coApplicant.phone] && this.state.isInvalid[coApplicant.phone]
                              ? this.state.isInvalid[coApplicant.phone]
                              : ''
                          }
                        />
                        <TextField
                          label="Email"
                          name={coApplicant.email}
                          id={coApplicant.email}
                          required={false}
                          disabled={quickCheckProcessing}
                          onChange={this.onChange}
                          onBlur={this.onBlur}
                          type="text"
                          maxLength="5"
                          minLength="50"
                          value={formValues[coApplicant.email] || ''}
                          variant="outlined"
                          className="text-field"
                          fullWidth
                          error={!!this.state.isInvalid[coApplicant.email]}
                          helperText={
                            this.state.isInvalid[coApplicant.email] ? this.state.isInvalid[coApplicant.email] : ''
                          }
                        />
                      </div>
                      <EligibilityInformationDetail
                        {...{
                          formFields: coApplicantEligibility,
                          onChange: this.onChange,
                          onBlur: this.onBlur,
                          dense: false,
                          formOptions: {
                            dateOfBirth: {
                              required: false,
                              label: 'Date of Birth (MM/DD/YYYY - optional)',
                              initialValue: this.state.formValues[coApplicantEligibility.dateOfBirth],
                            },
                            social: {
                              required: true,
                              label: 'Last 4 digits of SSN (e.g. 6789) *',
                              digits: 4,
                              initialValue: this.state.formValues[coApplicantEligibility.social],
                            },
                            annualIncome: {
                              required: false,
                              label: 'Annual Income (optional)¹',
                              initialValue: this.state.formValues[coApplicantEligibility.annualIncome],
                            },
                          },
                        }}
                      />
                    </div>
                  </>
                )}
                {!this.state.coAppOpen && (
                  <Anchor className="add-button add-coapp-button" onClick={this.onClickShowHideCoApp}>
                    <span className="plus">+</span>
                    <div className="label">Add a co-applicant</div>
                  </Anchor>
                )}
                <div className="row">
                  By clicking "Check Now" below, I confirm that the individual named above has provided consent for the
                  Lender, its assignees, or servicers to access their credit report and post a soft inquiry for the
                  purpose of pre-qualifying for home efficiency financing through a financing partner.
                  {!isInstallerUser && (
                    <>
                      {' '}
                      This site is protected by reCAPTCHA and the Google{' '}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </a>{' '}
                      apply.
                    </>
                  )}
                </div>
                {this.renderButtons()}
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default QuickCheckPage;
